import React, { useEffect, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Header from '@components/Base/Header';
import WorkFlowList from '@components/WorkFlow';
import messages from '@containers/workflows/messages';
import { getWorkflowCandidateListAction, getWtfWorkflowAction } from '@containers/workflows/slice';
import { getMixpanelProperties } from '@utils/helpers';
import { useAppDispatch, useAppSelector } from '@utils/hooks';
import { proviewConsoleExamWorkflowPageViewedEvent } from '@utils/mixpanelActions';
import { RootState } from '@utils/store';

import {
  formatInitialFilters,
  getFormattedFilters,
  getFormattedSearchConditions,
  getModifiedUserList,
} from './helpers';
import { FormatedWorkflowList, Workflow } from './types';

export type AppliedFilters = {
  selectedStatus?: { label: string; value: string }[];
  selectedUser?: { label: string; value: string }[];
  selectedWorkflow?: { label: string; value: string }[];
};

const WorkFlows: React.FC = () => {
  const dispatch = useAppDispatch();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const parseInitialFilters = formatInitialFilters(searchParams);
  const filterDetails = parseInitialFilters();
  const { filters, search } = filterDetails;
  const [appliedFilters, updateAppliedFilters] = useState<AppliedFilters>(filters);
  const { data: wftWorkflow, isLoading, totalCount } = useAppSelector((state: RootState) => state.workFlowList);
  const { user, isMixpanelInitialized } = useAppSelector((state) => state.app);
  const [allWorkflows, setAllWorkflows] = useState<FormatedWorkflowList[]>([]);

  const [searchTerm, setSearchTerm] = useState<string>(search);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const userList = useMemo(() => {
    return allWorkflows.length ? getUserList(allWorkflows) : [];
  }, [allWorkflows]);

  const workflowsList = useMemo(() => {
    return allWorkflows.map((workflow) => ({
      label: workflow.title,
      value: workflow.title,
    }));
  }, [allWorkflows]);

  const updateURLParams = () => {
    setSearchParams(
      (params) => {
        const filters = Object.entries(appliedFilters).reduce((acc: Record<string, string | object>, [key, value]) => {
          if (value) {
            if (
              Array.isArray(value) ? value.length > 0 : typeof value === 'object' ? Object.keys(value).length > 0 : true
            ) {
              acc[key] = value;
            }
          }
          return acc;
        }, {});

        if (Object.keys(filters).length > 0) {
          params.set('filters', JSON.stringify(filters));
        } else {
          params.delete('filters');
        }

        if (searchTerm) {
          params.set('search', searchTerm.replace(/%/g, ''));
        } else {
          params.delete('search');
        }
        return params;
      },
      { replace: true },
    );
  };

  const handleStepClick = (workflow: Workflow) => {
    const payload = {
      workflow_id: workflow.id,
      step_id: workflow.steps[0].id,
    };
    dispatch(getWorkflowCandidateListAction(payload));
    navigate(`/workflow/${payload.workflow_id}/candidates/${payload.step_id}`);
  };

  const handleNavigateToExam = () => {
    navigate(`/exams`);
  };

  const handleDispatchWtfWorkflowAction = (payload: {
    courseId: number;
    searchConditions: object[] | object;
    limit: number;
    offset: number;
    conditions: object;
    callback?: {
      onSuccess: (res: unknown) => void;
    };
  }) => {
    dispatch(getWtfWorkflowAction(payload));
  };

  useEffect(() => {
    if (courseId) {
      dispatch(
        getWtfWorkflowAction({
          courseId: parseInt(courseId),
          searchConditions: searchTerm ? getFormattedSearchConditions(searchTerm) : {},
          conditions: {},
          limit: 0,
          offset: 0,
          callback: {
            onSuccess: (res) => {
              setAllWorkflows(res as FormatedWorkflowList[]);
            },
          },
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (courseId) {
      const payload = {
        courseId: parseInt(courseId),
        searchConditions: searchTerm ? getFormattedSearchConditions(searchTerm) : {},
        conditions: getFormattedFilters(appliedFilters as AppliedFilters),
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      };
      updateURLParams();
      handleDispatchWtfWorkflowAction(payload);
    }
  }, [pagination]);

  useEffect(() => {
    if (courseId) {
      const payload = {
        courseId: parseInt(courseId),
        searchConditions: searchTerm ? getFormattedSearchConditions(searchTerm) : {},
        conditions: getFormattedFilters(appliedFilters as AppliedFilters),
        limit: pagination.pageSize,
        offset: 0,
      };
      updateURLParams();
      setPagination({ pageIndex: 0, pageSize: 10 });
      handleDispatchWtfWorkflowAction(payload);
    }
  }, [appliedFilters, searchTerm]);

  useEffect(() => {
    if (isMixpanelInitialized && user) {
      const mixpanelProperties = getMixpanelProperties(user);
      const additionalProperties = {
        module: 'Exams',
      };
      const properties = { ...mixpanelProperties, ...additionalProperties };
      proviewConsoleExamWorkflowPageViewedEvent(properties);
    }
  }, [isMixpanelInitialized, user.tenant]);

  return (
    <>
      <Header details={{ label: <FormattedMessage {...messages.workflow} /> }} />
      <div className="flex w-full px-8 pt-3 ml-2 text-gray-500 text-sm font-medium">
        <button className="cursor-pointer" data-testid="exam-btn" onClick={handleNavigateToExam}>
          {wftWorkflow.length && wftWorkflow[0].course?.name ? wftWorkflow[0].course?.name : 'Exam'}
        </button>{' '}
        <span className="px-2">{'>'}</span>{' '}
        <span>
          <FormattedMessage {...messages.workflow} />
        </span>
      </div>
      <WorkFlowList
        wftWorkflow={wftWorkflow}
        workflowsList={workflowsList}
        appliedFilters={appliedFilters}
        updateAppliedFilters={updateAppliedFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isLoading={isLoading}
        isMixpanelInitialized={isMixpanelInitialized}
        user={user}
        totalCount={totalCount}
        pagination={pagination}
        setPagination={setPagination}
        userList={userList}
        handleStepClick={handleStepClick}
      />
    </>
  );
};

export default WorkFlows;

export const getUserList = (allWorkflows: FormatedWorkflowList[]): { label: string; value: number }[] => {
  return getModifiedUserList(allWorkflows).filter(
    (user): user is { label: string; value: number } => user.value !== undefined,
  );
};
